import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeamProvider } from './context/TeamContext';
import { Toaster } from './components/ui/toaster';
import Login from './components/Login';
import VerifyEmail from './pages/VerifyEmail';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import League from './pages/League';
import Playlists from './pages/Playlists';
import Notes from './pages/Notes';
import Playbook from './pages/Playbook';
import TeamProfile from './pages/TeamProfile';
import Roster from './pages/Roster';
import Training from './pages/Training';
import TrainingSession from './pages/TrainingSession';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Feed from './pages/Feed';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import CreatePost from './pages/CreatePost';
import PostDetail from './pages/PostDetail';
import AdminPanel from './pages/AdminPanel';
import AllUsers from './pages/AllUsers';
import PlayerStats from './pages/PlayerStats';
import Videos from './pages/Videos';
import VideoDetail from './pages/VideoDetail';
import PlaylistDetail from './pages/PlaylistDetail';
import LegalDetail from './components/LegalDetail';
import Market from './pages/Market';
import MobileBottomNav from './components/MobileBottomNav';
import MobileTeamMenu from './pages/MobileTeamMenu';
import MobileHeader from './components/MobileHeader';
import Messages from './pages/Messages';
import Chat from './pages/Chat';
import './i18n/config';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-[#000] overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <MobileHeader />
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          {children}
        </main>
      </div>
      <MobileBottomNav />
    </div>
  );
};

// Public route wrapper - shows layout with sidebar (limited) for all users
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-[#000] overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <MobileHeader />
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          {children}
        </main>
      </div>
      <MobileBottomNav />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <TeamProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/legal" element={<LegalDetail />} />
            <Route path="/terms" element={<Navigate to="/legal?type=TERMS" replace />} />
            <Route path="/privacy" element={<Navigate to="/legal?type=PRIVACY" replace />} />

            {/* Public Routes - accessible without login */}
            <Route
              path="/feed"
              element={
                <PublicRoute>
                  <Feed />
                </PublicRoute>
              }
            />
            <Route
              path="/league"
              element={
                <PublicRoute>
                  <League />
                </PublicRoute>
              }
            />
            <Route
              path="/stats"
              element={
                <PublicRoute>
                  <PlayerStats />
                </PublicRoute>
              }
            />
            <Route
              path="/profile/:username"
              element={
                <PublicRoute>
                  <Profile />
                </PublicRoute>
              }
            />
            <Route
              path="/teams/:teamId/profile"
              element={
                <PublicRoute>
                  <TeamProfile />
                </PublicRoute>
              }
            />
            <Route
              path="/post/:id"
              element={
                <PublicRoute>
                  <PostDetail />
                </PublicRoute>
              }
            />
            <Route
              path="/market"
              element={
                <PublicRoute>
                  <Market />
                </PublicRoute>
              }
            />

            {/* Protected Routes - require login */}
            <Route
              path="/mobile-menu"
              element={
                <ProtectedRoute>
                  <MobileTeamMenu />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists"
              element={
                <ProtectedRoute>
                  <Playlists />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playlists/:playlistId"
              element={
                <ProtectedRoute>
                  <PlaylistDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notes"
              element={
                <ProtectedRoute>
                  <Notes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbook"
              element={
                <ProtectedRoute>
                  <Playbook />
                </ProtectedRoute>
              }
            />
            <Route
              path="/roster"
              element={
                <ProtectedRoute>
                  <Roster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training"
              element={
                <ProtectedRoute>
                  <Training />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/:id"
              element={
                <ProtectedRoute>
                  <TrainingSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/me/edit"
              element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/post/create"
              element={
                <ProtectedRoute>
                  <CreatePost />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/videos"
              element={
                <ProtectedRoute>
                  <Videos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/videos/:id"
              element={
                <ProtectedRoute>
                  <VideoDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AllUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages/:userId"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/feed" replace />} />
            <Route path="*" element={<Navigate to="/feed" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </TeamProvider>
    </AuthProvider>
  );
}

export default App;
